import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { ShopUserQuery } from "#web/components/shop/queries/shopUserQuery";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { Modal, TitleBar, useAppBridge } from "@shopify/app-bridge-react";
import { Banner, Box, TextField } from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

const en = {
  cancel: "Cancel",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the feedback modal",
  },
  feedback: "Feedback",
  label: "Feedback",
  placeholder: "Let us know what you think...",
  submit: "Submit",
  success: "Feedback sent",
  title: "Send us your feedback",
};

export const FeedbackModalId = "feedback-modal";

export const FeedbackModal = () => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const { data: shopUser, isError: shopUserError, isFetching: shopUserFetching } = useQuery(ShopUserQuery());
  const shopify = useAppBridge();

  const [feedbackContent, setFeedbackContent] = useState<string>("");
  const handleFeedbackContentChange = useCallback((newValue: string) => {
    setFeedbackContent(newValue);
  }, []);

  const handleSubmit = async () => {
    if (!shop || !shopUser) {
      return;
    }

    await api.shopifyShop.sendFeedback(shop.id, {
      feedback: feedbackContent,
      userEmail: shopUser.email,
    });
    void shopify.modal.hide(FeedbackModalId);
    shopify.toast.show(t("success"));
  };

  const closeModal = () => {
    void shopify.modal.hide(FeedbackModalId);
  };

  const isLoading = shopFetching || shopUserFetching;
  const isError = shopError || shopUserError;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  return (
    <Modal id={FeedbackModalId}>
      <Box paddingBlock="400" paddingInline="600">
        <TextField
          autoComplete="off"
          label={t("label")}
          labelHidden
          multiline={6}
          onChange={handleFeedbackContentChange}
          placeholder={t("placeholder")}
          value={feedbackContent}
        />
      </Box>
      <TitleBar title={t("title")}>
        <button disabled={isLoading || !feedbackContent} onClick={void handleSubmit} variant="primary">
          {t("submit")}
        </button>
        <button onClick={closeModal}>{t("cancel")}</button>
      </TitleBar>
    </Modal>
  );
};
